<template>
  <Layout>
    <div class="campaign-list">
      <div class="page-header">
        <div>
          <h2>广告计划列表</h2>
          <p>管理所有广告计划</p>
        </div>
        <el-button type="primary" @click="$router.push('/campaigns/create')">
          创建广告计划
        </el-button>
      </div>
      
      <!-- 筛选条件 -->
      <el-card shadow="never" class="filter-container">
        <el-form :inline="true" :model="filterForm" class="filter-form">
          <el-form-item label="广告活动">
            <el-select v-model="filterForm.activityId" placeholder="选择活动" clearable filterable>
              <el-option
                v-for="activity in campaignActivities"
                :key="activity.id"
                :label="activity.name"
                :value="activity.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="计划类型">
            <el-select v-model="filterForm.campaignType" placeholder="选择类型" clearable>
              <el-option label="标准广告" value="standard"></el-option>
              <el-option label="软文广告" value="article"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="filterForm.status" placeholder="选择状态" clearable>
              <el-option label="审核中" value="reviewing"></el-option>
              <el-option label="运行中" value="active"></el-option>
              <el-option label="已暂停" value="paused"></el-option>
              <el-option label="已停止" value="stopped"></el-option>
              <el-option label="已完成" value="completed"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="关键词">
            <el-input v-model="filterForm.keyword" placeholder="输入关键词"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleFilter">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>
      
      <!-- 广告计划列表 -->
      <el-table
        v-loading="loading"
        :data="pagedCampaigns"
        border
        style="width: 100%; margin-top: 20px;">
        <el-table-column prop="name" label="计划名称" min-width="240" show-overflow-tooltip></el-table-column>
        <el-table-column prop="activity_name" label="广告活动" min-width="180">
          <template slot-scope="scope">
            <span>{{ scope.row.activity_name || '未分配活动' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="campaign_type" label="类型" width="110">
          <template slot-scope="scope">
            <el-tag :type="scope.row.campaign_type === 'article' ? 'warning' : 'success'">
              {{ formatCampaignType(scope.row.campaign_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)">{{ formatStatus(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="180"></el-table-column>
        <el-table-column prop="update_time" label="更新时间" width="180"></el-table-column>
        <el-table-column fixed="right" label="操作" width="200">
          <template slot-scope="scope">
            <el-button @click="$router.push(`/campaigns/edit/${scope.row.id}`)" type="text" size="small">编辑</el-button>
            <el-button v-if="scope.row.status === 'active'" @click="updateStatus(scope.row.id, 'paused')" type="text" size="small">暂停</el-button>
            <el-button v-else-if="scope.row.status === 'paused'" @click="updateStatus(scope.row.id, 'active')" type="text" size="small">启动</el-button>
            <el-button v-if="scope.row.status !== 'stopped'" @click="updateStatus(scope.row.id, 'stopped')" type="text" size="small">停止</el-button>
            <el-button @click="handleDelete(scope.row)" type="text" size="small" class="delete-btn">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 30, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCampaigns">
        </el-pagination>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { mapState } from 'vuex'

export default {
  name: 'CampaignList',
  components: {
    Layout
  },
  data() {
    return {
      filterForm: {
        activityId: '',
        campaignType: '',
        status: '',
        keyword: ''
      },
      currentPage: 1,
      pageSize: 30
    }
  },
  computed: {
    ...mapState(['campaigns', 'campaignActivities', 'loading']),
    filteredCampaigns() {
      let result = [...this.campaigns]

      if (this.filterForm.activityId) {
        result = result.filter(item => item.activity_id === this.filterForm.activityId)
      }

      if (this.filterForm.campaignType) {
        result = result.filter(item => (item.campaign_type || 'standard') === this.filterForm.campaignType)
      }
      
      // 按状态筛选
      if (this.filterForm.status) {
        result = result.filter(item => item.status === this.filterForm.status)
      }
      
      // 按关键词筛选
      if (this.filterForm.keyword) {
        const keyword = this.filterForm.keyword.toLowerCase()
        result = result.filter(item => 
          item.name.toLowerCase().includes(keyword) || 
          (item.creative && item.creative.title && item.creative.title.toLowerCase().includes(keyword))
        )
      }
      
      // 按创建时间排序
      return result.sort((a, b) => new Date(b.create_time) - new Date(a.create_time))
    },
    pagedCampaigns() {
      const startIndex = (this.currentPage - 1) * this.pageSize
      const endIndex = startIndex + this.pageSize
      return this.filteredCampaigns.slice(startIndex, endIndex)
    },
    totalCampaigns() {
      return this.filteredCampaigns.length
    }
  },
  watch: {
    filterForm: {
      deep: true,
      handler() {
        this.currentPage = 1
      }
    },
    totalCampaigns(total) {
      const maxPage = Math.max(1, Math.ceil(total / this.pageSize))
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage
      }
    }
  },
  methods: {
    getStatusType(status) {
      switch (status) {
        case 'reviewing':
          return 'warning'
        case 'active':
          return 'success'
        case 'paused':
          return 'warning'
        case 'stopped':
          return 'danger'
        case 'completed':
          return 'success'
        default:
          return 'info'
      }
    },
    formatStatus(status) {
      switch (status) {
        case 'reviewing':
          return '审核中'
        case 'active':
          return '运行中'
        case 'paused':
          return '已暂停'
        case 'stopped':
          return '已停止'
        case 'completed':
          return '已完成'
        default:
          return '未知'
      }
    },
    formatCampaignType(campaignType) {
      return campaignType === 'article' ? '软文广告' : '标准广告'
    },
    handleFilter() {
      this.currentPage = 1
    },
    resetFilter() {
      this.filterForm = {
        activityId: '',
        campaignType: '',
        status: '',
        keyword: ''
      }
      this.currentPage = 1
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    async updateStatus(campaignId, newStatus) {
      const campaign = this.campaigns.find(item => item.id === campaignId)
      if (!campaign) return
      
      const updatedCampaign = { ...campaign, status: newStatus }
      const success = await this.$store.dispatch('updateCampaign', {
        id: campaignId,
        data: updatedCampaign
      })
      
      if (success) {
        this.$message.success(`广告计划状态已更新为"${this.formatStatus(newStatus)}"`)
      } else {
        this.$message.error('更新广告计划状态失败')
      }
    },
    handleDelete(campaign) {
      this.$confirm(`确定删除广告计划"${campaign.name}"吗?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const success = await this.$store.dispatch('deleteCampaign', campaign.id)
        if (success) {
          this.$message.success('删除成功')
        } else {
          this.$message.error('删除失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    }
  },
  created() {
    this.$store.dispatch('fetchCampaignActivities')
    this.$store.dispatch('fetchCampaigns')
  }
}
</script>

<style scoped>
.campaign-list {
  padding: 20px 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-weight: normal;
}

.page-header p {
  margin: 5px 0 0;
  color: #909399;
}

.filter-container {
  margin-bottom: 20px;
}

.filter-form {
  display: flex;
  align-items: center;
}

.delete-btn {
  color: #F56C6C;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style> 
